/* Minimal Preset Regime Cache panel.
 *
 * Shows the cache status for the currently-selected research preset
 * + current Regime Gate classifier config. One-button precompute.
 *
 * Research/observability only — never mutates strategy or live state.
 * Independent of the Regime Gate toggle: the preset cache can be
 * warmed even when the gate is disabled, because the cached series
 * is shared across every backtest hitting the same window+cfg.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

function _formatAxiosError(e) {
  // Surface method + URL + status + server detail when available, so a
  // future 404/500 here points the operator straight at the failing
  // endpoint instead of axios's generic "Request failed with status
  // code 404" string. Keeps the toast actionable without leaking
  // sensitive headers.
  const cfg = e?.config || {};
  const method = String(cfg.method || "?").toUpperCase();
  const url = (cfg.baseURL || "") + (cfg.url || "");
  const status = e?.response?.status;
  const serverDetail = e?.response?.data?.detail
    || e?.response?.statusText
    || e?.message
    || String(e);
  if (status) {
    return `${method} ${url} → ${status}: ${serverDetail}`;
  }
  return `${method} ${url}: ${serverDetail}`;
}

function _percentRow(pct) {
  if (!pct) return null;
  const labels = ["UPTREND", "DOWNTREND", "CHOP"];
  return (
    <div className="flex gap-2 text-[10px] font-mono">
      {labels.map((lbl) => {
        if (pct[lbl] === undefined) return null;
        const color =
          lbl === "UPTREND"   ? "text-[#10B981]" :
          lbl === "DOWNTREND" ? "text-[#FF3B30]" :
                                "text-[#FBBF24]";
        return (
          <span key={lbl} className={color}>
            {lbl}: {pct[lbl]}%
          </span>
        );
      })}
    </div>
  );
}

export default function RegimePresetCachePanel({
  symbol,
  preset,
  regimeGate,
  testidPrefix = "bt",
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Determine the active classifier config — falls back to baseline
  // (EMA 21/100 × 1h) when the gate config doesn't specify.
  const classifierCfg = {
    regime_timeframe:     regimeGate?.regime_timeframe || "2h",
    ema_fast:             21,
    ema_slow:             100,
    ema_slope_lookback:   20,
    adx_length:           14,
    adx_chop_threshold:   18.0,
    adx_trend_threshold:  20.0,
    adx_strong_threshold: 25.0,
  };

  // Resolve the window from the preset. Composites have multiple
  // windows; we only handle single-window presets in this minimal UI.
  // Caller can still warm composites preset-by-preset via the
  // command-line tool.
  const window = (() => {
    if (!preset) return null;
    if (preset.type === "single") {
      return { start_iso: preset.start_iso, end_iso: preset.end_iso };
    }
    return null;
  })();

  const refresh = useCallback(async () => {
    if (!window || !preset) {
      setStatus(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const r = await api.regimeCacheStatus({
        symbol,
        ...window,
        ...classifierCfg,
        preset_id:   preset.id,
        preset_name: preset.label,
      });
      setStatus(r);
    } catch (e) {
      setError(_formatAxiosError(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, preset?.id, classifierCfg.regime_timeframe]);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [refresh]);

  const onPrecompute = useCallback(async () => {
    if (!window || !preset) return;
    setLoading(true);
    setError("");
    try {
      await api.regimeCachePrecompute({
        symbol,
        ...window,
        ...classifierCfg,
        preset_id:              preset.id,
        preset_name:            preset.label,
        preset_expected_label:  preset.regime_type,
      });
      await refresh();
    } catch (e) {
      setError(_formatAxiosError(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, preset?.id, classifierCfg.regime_timeframe]);

  if (!preset) return null;
  if (preset.type !== "single") {
    return (
      <div
        data-testid={`${testidPrefix}-regime-preset-cache-composite-skip`}
        className="text-[10px] font-mono text-[#71717A]"
      >
        Regime preset cache: composite presets unsupported in UI — use CLI
      </div>
    );
  }

  return (
    <div
      data-testid={`${testidPrefix}-regime-preset-cache-panel`}
      className="flex items-center gap-3 px-2 py-1 bg-[#0D0D12] border border-[#27272A] rounded-sm"
    >
      <span className="text-[10px] font-mono text-[#71717A]">
        Regime cache · {classifierCfg.regime_timeframe} ·
        EMA {classifierCfg.ema_fast}/{classifierCfg.ema_slow}
      </span>
      {status?.cached ? (
        <>
          <span
            data-testid={`${testidPrefix}-regime-preset-cache-warm`}
            className="text-[10px] font-mono text-[#10B981]"
          >
            ✓ warm ({status.n_candles}c)
          </span>
          {_percentRow(status.distribution?.percent)}
          <span className="text-[10px] font-mono text-[#71717A]">
            expected: <span className="text-[#A0A0AB]">{preset.regime_type}</span>
          </span>
        </>
      ) : (
        <span
          data-testid={`${testidPrefix}-regime-preset-cache-cold`}
          className="text-[10px] font-mono text-[#A0A0AB]"
        >
          cold
        </span>
      )}
      <button
        type="button"
        data-testid={`${testidPrefix}-regime-preset-precompute-btn`}
        disabled={loading}
        onClick={onPrecompute}
        className={`px-2 py-0.5 text-[10px] font-mono rounded-sm border ${
          loading
            ? "border-[#3F3F46] text-[#71717A]"
            : "border-[#3F3F46] text-[#E4E4E7] hover:bg-[#1A1A22]"
        }`}
      >
        {loading ? "..." : (status?.cached ? "Recompute" : "Precompute")}
      </button>
      {error ? (
        <span
          data-testid={`${testidPrefix}-regime-preset-cache-error`}
          className="text-[10px] font-mono text-[#FF3B30]"
          title={error}
        >
          {error.slice(0, 160)}
        </span>
      ) : null}
    </div>
  );
}
